import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, size) => {
    setCart(currentCart => {
      // Check if item already exists
      const existingIndex = currentCart.findIndex(
        item => item.product_id === product.id && item.size === size.size
      );

      if (existingIndex > -1) {
        // Check if we can add more
        const currentQuantity = currentCart[existingIndex].quantity;
        if (currentQuantity >= size.quantity) {
          // Show error toast - max quantity reached
          return currentCart; // Don't add more
        }
        // Update quantity
        const newCart = [...currentCart];
        newCart[existingIndex].quantity += 1;
        return newCart;
      } else {
        // Add new item
        return [
          ...currentCart,
          {
            product_id: product.id,
            name: product.name,
            size: size.size,
            price: size.price,
            quantity: 1,
            image: product.image,
            max_quantity: size.quantity
          }
        ];
      }
    });
  };

  const removeFromCart = (productId, size) => {
    setCart(currentCart =>
      currentCart.filter(
        item => !(item.product_id === productId && item.size === size)
      )
    );
  };

  const updateQuantity = (productId, size, quantity, maxQuantity) => {
    if (quantity <= 0) {
      removeFromCart(productId, size);
      return;
    }

    // Validate against max quantity
    if (maxQuantity && quantity > maxQuantity) {
      return; // Don't allow exceeding max
    }

    setCart(currentCart =>
      currentCart.map(item =>
        item.product_id === productId && item.size === size
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('cart');
  };

  const getCartCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartCount,
    getCartTotal
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
